export class MessageSyntaxError extends Error {
}
class Parser {
    source;
    at = 0;
    constructor(source) {
        this.source = source;
    }
    parse() {
        const nodes = this.nodes(false, false);
        if (this.at < this.source.length)
            throw new MessageSyntaxError(`Unexpected "}" at ${this.at}`);
        return nodes;
    }
    nodes(nested, inPlural) {
        const out = [];
        let text = '';
        const flush = () => {
            if (text)
                out.push({ kind: 'text', value: text });
            text = '';
        };
        while (this.at < this.source.length) {
            const character = this.source[this.at];
            if (character === '}') {
                if (!nested)
                    throw new MessageSyntaxError(`Unbalanced "}" at ${this.at}`);
                break;
            }
            if (character === "'" && this.source[this.at + 1] === "'") {
                text += "'";
                this.at += 2;
                continue;
            }
            if (character === "'" && (this.source[this.at + 1] === '{' || this.source[this.at + 1] === '}' || this.source[this.at + 1] === '#')) {
                const end = this.source.indexOf("'", this.at + 1);
                if (end < 0)
                    throw new MessageSyntaxError('Unclosed quote');
                text += this.source.slice(this.at + 1, end);
                this.at = end + 1;
                continue;
            }
            if (character === '#' && inPlural) {
                flush();
                out.push({ kind: 'pound' });
                this.at += 1;
                continue;
            }
            if (character === '{') {
                flush();
                out.push(this.argument());
                continue;
            }
            text += character;
            this.at += 1;
        }
        flush();
        return out;
    }
    word() {
        this.space();
        const start = this.at;
        while (this.at < this.source.length && /[A-Za-z0-9_=.-]/.test(this.source[this.at]))
            this.at += 1;
        if (start === this.at)
            throw new MessageSyntaxError(`Expected a name at ${this.at}`);
        return this.source.slice(start, this.at);
    }
    space() {
        while (this.at < this.source.length && /\s/.test(this.source[this.at]))
            this.at += 1;
    }
    expect(character) {
        this.space();
        if (this.source[this.at] !== character)
            throw new MessageSyntaxError(`Expected "${character}" at ${this.at}`);
        this.at += 1;
    }
    argument() {
        this.expect('{');
        const name = this.word();
        this.space();
        if (this.source[this.at] === '}') {
            this.at += 1;
            return { kind: 'arg', name };
        }
        this.expect(',');
        const type = this.word();
        if (type === 'number') {
            this.expect('}');
            return { kind: 'number', name };
        }
        if (type !== 'plural' && type !== 'select')
            throw new MessageSyntaxError(`Unknown argument type "${type}"`);
        this.expect(',');
        let offset = 0;
        const options = {};
        for (;;) {
            this.space();
            if (this.source[this.at] === '}') {
                this.at += 1;
                break;
            }
            const key = this.word();
            if (key.startsWith('offset:')) {
                offset = Number(key.slice(7));
                continue;
            }
            this.expect('{');
            options[key] = this.nodes(true, type === 'plural');
            this.expect('}');
        }
        if (!options.other)
            throw new MessageSyntaxError(`"${name}" needs an "other" option`);
        return type === 'plural' ? { kind: 'plural', name, offset, options } : { kind: 'select', name, options };
    }
}
export function parseMessage(source) {
    return new Parser(source).parse();
}
export function messageArguments(nodes, into = new Set()) {
    for (const node of nodes) {
        if (node.kind === 'arg' || node.kind === 'number')
            into.add(node.name);
        if (node.kind === 'plural' || node.kind === 'select') {
            into.add(node.name);
            for (const option of Object.values(node.options))
                messageArguments(option, into);
        }
    }
    return into;
}
const MAX_COMPILED = 2_000;
const compiled = new Map();
function compile(source) {
    const found = compiled.get(source);
    if (found)
        return found;
    const nodes = parseMessage(source);
    if (compiled.size >= MAX_COMPILED) {
        const oldest = compiled.keys().next().value;
        if (oldest !== undefined)
            compiled.delete(oldest);
    }
    compiled.set(source, nodes);
    return nodes;
}
const pluralRules = new Map();
function pluralCategory(locale, value) {
    let rules = pluralRules.get(locale);
    if (!rules) {
        rules = new Intl.PluralRules(locale === 'ur-Latn' ? 'ur' : locale);
        pluralRules.set(locale, rules);
    }
    return rules.select(value);
}
function render(nodes, params, locale, options, pound) {
    let out = '';
    const escape = options.escape ?? ((value) => value);
    for (const node of nodes) {
        switch (node.kind) {
            case 'text':
                out += options.escape ? escape(node.value) : node.value;
                break;
            case 'arg':
                out += escape(String(params[node.name] ?? ''));
                break;
            case 'number': {
                const value = Number(params[node.name] ?? 0);
                out += escape(new Intl.NumberFormat(options.numberLocale ?? 'en').format(value));
                break;
            }
            case 'pound':
                out += pound === null ? '#' : new Intl.NumberFormat(options.numberLocale ?? 'en').format(pound);
                break;
            case 'plural': {
                const value = Number(params[node.name] ?? 0);
                const exact = node.options[`=${value}`];
                const branch = exact ?? node.options[pluralCategory(locale, value - node.offset)] ?? node.options.other;
                out += render(branch, params, locale, options, value - node.offset);
                break;
            }
            case 'select': {
                const branch = node.options[String(params[node.name] ?? 'other')] ?? node.options.other;
                out += render(branch, params, locale, options, pound);
                break;
            }
        }
    }
    return out;
}
export function formatMessage(source, params = {}, locale = 'en', options = {}) {
    return render(compile(source), params, locale, options, null);
}
//# sourceMappingURL=format.js.map